import { computed, reactive, Ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import api from "@/services/api";
import { PaginatedResponse } from "@/services/utils";

/**
 * AI Agent Context - captured from current view state
 * Sent with the first message to provide context to the AI agent
 */
export interface AIAgentContext {
  // View information
  viewName?: string; // e.g., "marketing_performance"
  routePath?: string; // e.g., "/marketing/performance"

  // Dashboard context - when viewing/editing a dashboard
  dashboardId?: number;
  dashboardTab?: { index: number; name: string };

  // Table aggregation level (from tableLevel query param)
  // Indicates which level of data the user is viewing: campaign, ad_group, ad, keyword, etc.
  tableLevel?: string;

  // Global filters from data_filters store
  temporalFilter?: {
    selection: { preset: string; start: string; end: string };
    comparison?: { preset: string; start: string; end: string };
  };
  includedResources?: number[];
  includedChannels?: string[];
  includedTags?: number[];
  excludedTags?: number[];
  includedConversionGroups?: number[];
  includedAdNetworks?: string[];

  // Entity drill-down from route.query
  entityContext?: {
    level: string; // 'campaign', 'adGroup', 'ad', etc.
    id: number;
    name?: string; // Optional - may not be in URL
    channel?: string; // Optional - may not be in URL
  };

  // Page-specific filters
  metricFilters?: {
    metric: string;
    operator: string;
    value: number;
  }[];
}

/**
 * AI Agent Session - conversation session
 */
export interface AIAgentSession {
  id: number;
  title: string;
  provider: string;
  model: string;
  status: "active" | "completed" | "error" | "archived";
  totalIterations: number;
  creditsUsed: string; // DRF-serialized Decimal, e.g. "12.7500"
  // Which effort levels this session's model accepts, served by the backend.
  // Empty means the model takes no effort setting, so there is no control to
  // show. Optional because a cached session payload predating this field
  // deserializes without it.
  supportedReasoningEfforts?: ReasoningEffort[];
  createdAt: string;
  modifiedAt: string;
}

/**
 * Reasoning effort levels supported by GPT-5-family models. Kept in sync
 * with `ALLOWED_REASONING_EFFORTS` in backend/ai_agents/serializers/session.py.
 */
export type ReasoningEffort = "minimal" | "low" | "medium" | "high";

export const REASONING_EFFORT_OPTIONS: {
  value: ReasoningEffort;
  label: string;
}[] = [
  { value: "minimal", label: "Minimal" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const DEFAULT_REASONING_EFFORT: ReasoningEffort = "low";

/**
 * File attachment metadata (images uploaded to CDN)
 */
export interface ChatAttachment {
  url: string;
  key: string;
  contentType: string;
  filename: string;
}

/**
 * AI Agent Message - individual message in a session
 */
export interface AIAgentMessage {
  id: number;
  role: "system" | "user" | "assistant" | "tool";
  content: string;
  attachments?: ChatAttachment[];
  toolCalls?: {
    id: string;
    name: string;
    arguments: Record<string, unknown>;
  }[];
  tokensUsed: number;
  createdAt: string;
}

/**
 * Tool Execution - result of a tool call
 */
export interface ToolExecution {
  toolName: string;
  arguments: Record<string, unknown>;
  result: string;
  success: boolean;
  durationMs: number;
}

/**
 * Chat Response - response from sending a message
 */
export interface ChatResponse {
  success: boolean;
  response: string;
  toolExecutions: ToolExecution[];
  iterations: number;
  tokensUsed: number;
  error?: string;
}

// List all sessions for the current user. The endpoint is paginated
// ({ count, next, previous, results }); unwrap `results` here so the query
// cache stays a plain array for all consumers (slice, setQueryData updates).
function listSessions(): Promise<AIAgentSession[]> {
  return api
    .get<PaginatedResponse<AIAgentSession>>("/ai-agents/sessions/")
    .then((response) => response.data.results);
}

export function useAISessionsQuery() {
  return reactive(
    useQuery({
      queryKey: ["ai_agent", "sessions"] as const,
      queryFn: listSessions,
      staleTime: 30 * 1000, // 30 seconds
    }),
  );
}

// Get a single session
function getSession(sessionId: number): Promise<AIAgentSession> {
  return api
    .get(`/ai-agents/sessions/${sessionId}/`)
    .then((response) => response.data);
}

export function useAISessionQuery(sessionId: Ref<number | null>) {
  return reactive(
    useQuery({
      queryKey: ["ai_agent", "session", sessionId] as const,
      queryFn: () => {
        if (sessionId.value === null) {
          return Promise.reject(new Error("Session ID is null"));
        }
        return getSession(sessionId.value);
      },
      enabled: computed(() => sessionId.value !== null),
      staleTime: 10 * 1000, // 10 seconds
    }),
  );
}

// Create new session
function createSession(data: {
  title?: string;
  provider?: string;
}): Promise<AIAgentSession> {
  const payload: { title: string; provider?: string } = {
    title: data.title || "New Chat",
  };
  // Only include provider if explicitly specified; otherwise let backend use org default
  if (data.provider) {
    payload.provider = data.provider;
  }
  return api
    .post("/ai-agents/sessions/", payload)
    .then((response) => response.data);
}

export function useCreateAISessionMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: createSession,
      onSuccess: (newSession) => {
        // Add to sessions list
        queryClient.setQueryData<AIAgentSession[]>(
          ["ai_agent", "sessions"],
          (sessions) => [newSession, ...(sessions ?? [])],
        );
        // Cache the new session
        queryClient.setQueryData<AIAgentSession>(
          ["ai_agent", "session", newSession.id],
          newSession,
        );
      },
    }),
  );
}

// Get messages for a session
function getMessages(sessionId: number): Promise<AIAgentMessage[]> {
  return api
    .get(`/ai-agents/sessions/${sessionId}/messages/`)
    .then((response) => response.data);
}

export function useAISessionMessagesQuery(sessionId: Ref<number | null>) {
  return reactive(
    useQuery({
      queryKey: ["ai_agent", "messages", sessionId] as const,
      queryFn: () => {
        if (sessionId.value === null) {
          return Promise.reject(new Error("Session ID is null"));
        }
        return getMessages(sessionId.value);
      },
      enabled: computed(() => sessionId.value !== null),
      // No polling - we use SSE streaming for real-time updates
    }),
  );
}

// Send message to session
function sendMessage({
  sessionId,
  message,
  context,
  effort,
}: {
  sessionId: number;
  message: string;
  context?: AIAgentContext;
  effort?: ReasoningEffort;
}): Promise<ChatResponse> {
  return api
    .post(`/ai-agents/sessions/${sessionId}/chat/`, {
      message,
      context,
      effort,
    })
    .then((response) => response.data);
}

export function useSendAIMessageMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: sendMessage,
      onSuccess: (_, variables) => {
        // Invalidate messages query to refetch
        queryClient.invalidateQueries({
          queryKey: ["ai_agent", "messages", variables.sessionId],
        });
        // Invalidate session query to update token count
        queryClient.invalidateQueries({
          queryKey: ["ai_agent", "session", variables.sessionId],
        });
        // Invalidate sessions list to update modified time
        queryClient.invalidateQueries({
          queryKey: ["ai_agent", "sessions"],
        });
      },
    }),
  );
}

// Delete session
function deleteSession(sessionId: number): Promise<void> {
  return api.delete(`/ai-agents/sessions/${sessionId}/`);
}

export function useDeleteAISessionMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteSession,
      onSuccess: (_, sessionId) => {
        // Remove from sessions list
        queryClient.setQueryData<AIAgentSession[]>(
          ["ai_agent", "sessions"],
          (sessions) => sessions?.filter((s) => s.id !== sessionId),
        );
        // Remove session cache
        queryClient.removeQueries({
          queryKey: ["ai_agent", "session", sessionId],
        });
        // Remove messages cache
        queryClient.removeQueries({
          queryKey: ["ai_agent", "messages", sessionId],
        });
      },
    }),
  );
}
